import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from '../config/env';
import type { SupportedLanguage } from '../i18n/languages';

export { AuthLoginPage } from './LoginPage';

type AuthUser = {
  sub?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string | null;
  role?: string;
  roles?: string[];
  platformAdmin?: boolean;
};

type StoredSession = {
  accessToken: string;
  refreshToken: string;
  user: AuthUser | null;
  expiresAt: number;
  remember: boolean;
};

type AuthContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  user: AuthUser | undefined;
  getAccessTokenSilently: (_options?: Record<string, unknown>) => Promise<string>;
  loginWithRedirect: (options?: Record<string, unknown> & { appState?: { returnTo?: string } }) => Promise<void>;
  logout: (options?: { logoutParams?: { returnTo?: string } }) => void;
  loginWithPassword: (
    email: string,
    password: string,
    options?: { remember?: boolean; language?: SupportedLanguage },
  ) => Promise<{ mustChangePassword: boolean; isPlatformAdmin: boolean }>;
};

const STORAGE_KEY = 'neoemr.portal.auth';
const LOGIN_PATH = '/login';
const DEFAULT_RETURN_TO = '/';
const TOKEN_REFRESH_SKEW_MS = 45_000;
const LOGIN_LANGUAGE_QUERY_PARAM = 'language';
/** One-time portal session handed over from an unlocked commercial offer. */
const HANDOFF_QUERY_PARAM = 'session';

const HOST_DEFAULT_LANGUAGE_MAP: Record<string, SupportedLanguage> = {
  'app.elenthor.com': 'en',
  'mi.elenthor.com': 'es',
  'it.elenthor.com': 'it',
};

const AuthContext = createContext<AuthContextValue | null>(null);

function createLoginRequiredError() {
  const error = new Error('Login required') as Error & { error?: string };
  error.error = 'login_required';
  return error;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length < 2) return {};
  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(window.atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function buildUserFromPayload(payload: Record<string, unknown>, currentUser?: AuthUser | null): AuthUser {
  const givenName = typeof payload.given_name === 'string' ? payload.given_name : currentUser?.given_name;
  const familyName = typeof payload.family_name === 'string' ? payload.family_name : currentUser?.family_name;
  const fullName =
    (typeof payload.name === 'string' && payload.name.trim()) ||
    [givenName, familyName].filter(Boolean).join(' ') ||
    currentUser?.name ||
    undefined;

  return {
    sub: typeof payload.sub === 'string' ? payload.sub : currentUser?.sub,
    email: typeof payload.email === 'string' ? payload.email : currentUser?.email,
    name: fullName,
    given_name: givenName,
    family_name: familyName,
    picture: typeof payload.picture === 'string' ? payload.picture : currentUser?.picture ?? null,
    role: typeof payload.role === 'string' ? payload.role : currentUser?.role,
    roles: Array.isArray(payload.roles)
      ? payload.roles.map((entry) => String(entry))
      : currentUser?.roles,
    platformAdmin: payload.platformAdmin === true || currentUser?.platformAdmin === true,
  };
}

function deriveExpiry(token: string) {
  const payload = decodeJwtPayload(token);
  const exp = typeof payload.exp === 'number' ? payload.exp : Number(payload.exp || 0);
  if (!Number.isFinite(exp) || exp <= 0) {
    return Date.now() + 15 * 60 * 1000;
  }
  return exp * 1000;
}

/**
 * Reads a one-time session token from the URL and removes it immediately.
 *
 * The token is stripped from the address bar before anything else runs so it
 * does not sit in a history entry, get copied out of the URL bar, or leak
 * through a Referer header on the next navigation.
 */
function consumeHandoffToken(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const token = params.get(HANDOFF_QUERY_PARAM);
  if (!token) return null;
  params.delete(HANDOFF_QUERY_PARAM);
  const query = params.toString();
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
  );
  return token.trim() || null;
}

function loadStoredSession() {
  const raw = window.localStorage.getItem(STORAGE_KEY) || window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed?.accessToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearStoredSession() {
  window.localStorage.removeItem(STORAGE_KEY);
  window.sessionStorage.removeItem(STORAGE_KEY);
}

function persistSession(session: StoredSession) {
  clearStoredSession();
  const serialized = JSON.stringify(session);
  if (session.remember) {
    window.localStorage.setItem(STORAGE_KEY, serialized);
  } else {
    window.sessionStorage.setItem(STORAGE_KEY, serialized);
  }
}

async function readResponseMessage(response: Response, fallback: string) {
  const text = await response.text().catch(() => '');
  if (!text.trim()) return fallback;
  try {
    const parsed = JSON.parse(text) as { message?: string; error?: string };
    return parsed.message || parsed.error || text;
  } catch {
    return text;
  }
}

function normalizeLoginLanguage(value?: unknown): SupportedLanguage | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('it')) return 'it';
  if (normalized.startsWith('ar')) return 'ar';
  if (normalized.startsWith('en')) return 'en';
  return null;
}

function inferLanguageFromPath(path?: string | null): SupportedLanguage | null {
  if (typeof path !== 'string') return null;
  const normalized = path.trim().toLowerCase();
  if (!normalized) return null;
  if (/^\/(?:es|es-co|es-es|es-mx)(?:\/|$)/.test(normalized)) return 'es';
  if (/^\/(?:it|it-it)(?:\/|$)/.test(normalized)) return 'it';
  if (/^\/(?:ar|ar-sa|ar-ae)(?:\/|$)/.test(normalized)) return 'ar';
  if (/^\/en(?:\/|$)/.test(normalized)) return 'en';
  return null;
}

function resolveHostDefaultLanguage(): SupportedLanguage | null {
  if (typeof window === 'undefined') return null;
  return HOST_DEFAULT_LANGUAGE_MAP[window.location.hostname] ?? null;
}

function resolveReferrerLanguage(): SupportedLanguage | null {
  if (typeof document === 'undefined' || !document.referrer) return null;
  try {
    const referrer = new URL(document.referrer);
    return inferLanguageFromPath(referrer.pathname) || HOST_DEFAULT_LANGUAGE_MAP[referrer.hostname] || null;
  } catch {
    return null;
  }
}

function resolveCurrentLoginLanguage(path?: string | null): SupportedLanguage {
  return (
    inferLanguageFromPath(path) ||
    resolveReferrerLanguage() ||
    resolveHostDefaultLanguage() ||
    (typeof document !== 'undefined' ? normalizeLoginLanguage(document.documentElement.lang) : null) ||
    (typeof navigator !== 'undefined' ? normalizeLoginLanguage(navigator.language) : null) ||
    'en'
  );
}

async function fetchCurrentUser(accessToken: string, currentUser?: AuthUser | null): Promise<AuthUser> {
  const payload = decodeJwtPayload(accessToken);
  if (payload.platformAdmin === true) {
    return buildUserFromPayload(payload, currentUser);
  }

  const response = await fetch(`${API_BASE}/auth/me`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(await readResponseMessage(response, 'Unable to load session.'));
  }

  const profile = (await response.json()) as {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    roles: string[];
  };

  return {
    sub: profile.id,
    email: profile.email,
    given_name: profile.firstName,
    family_name: profile.lastName,
    name: `${profile.firstName} ${profile.lastName}`.trim(),
    picture: null,
    role: profile.role,
    roles: profile.roles,
    platformAdmin: false,
  };
}

async function refreshSession(current: StoredSession): Promise<StoredSession> {
  if (!current.refreshToken) {
    throw createLoginRequiredError();
  }

  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      refreshToken: current.refreshToken,
    }),
  });

  if (!response.ok) {
    throw createLoginRequiredError();
  }

  const payload = (await response.json()) as { accessToken: string };
  const user = await fetchCurrentUser(payload.accessToken, current.user);
  const nextSession: StoredSession = {
    ...current,
    accessToken: payload.accessToken,
    user,
    expiresAt: deriveExpiry(payload.accessToken),
  };
  persistSession(nextSession);
  return nextSession;
}

function buildLoginUrl(returnTo?: string, language?: SupportedLanguage) {
  const params = new URLSearchParams();
  if (returnTo && returnTo.trim()) {
    params.set('returnTo', returnTo);
  }
  const resolvedLanguage = language || resolveCurrentLoginLanguage(returnTo);
  if (resolvedLanguage !== 'en') {
    params.set(LOGIN_LANGUAGE_QUERY_PARAM, resolvedLanguage);
  }
  const query = params.toString();
  return query ? `${LOGIN_PATH}?${query}` : LOGIN_PATH;
}

export function Auth0Provider({
  children,
}: {
  children: React.ReactNode;
  [key: string]: unknown;
}) {
  const [session, setSession] = useState<StoredSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const refreshPromiseRef = useRef<Promise<StoredSession> | null>(null);

  useEffect(() => {
    let active = true;
    const restore = async () => {
      // A demo handoff arrives as a token in the URL: the offer page has
      // already proved the visitor holds the offer password, and the API
      // minted a short portal session for a demo persona. Consumed BEFORE any
      // stored session so arriving with a demo link always shows the demo
      // patient rather than whoever used this browser last.
      const handoff = consumeHandoffToken();
      if (handoff) {
        try {
          const user = await fetchCurrentUser(handoff);
          const demoSession: StoredSession = {
            accessToken: handoff,
            // No refresh token: the demo session is deliberately finite, and
            // re-entry goes back through the offer password.
            refreshToken: '',
            user,
            expiresAt: deriveExpiry(handoff),
            // sessionStorage only -- a demo must not outlive the tab on what
            // may well be a shared machine.
            remember: false,
          };
          persistSession(demoSession);
          if (!active) return;
          setSession(demoSession);
          setError(null);
          setIsLoading(false);
          return;
        } catch {
          // Expired or rejected handoff: fall through to the normal path so
          // the visitor gets the login screen rather than a dead page.
        }
      }

      const stored = loadStoredSession();
      if (!stored) {
        if (active) setIsLoading(false);
        return;
      }

      try {
        const stillValid = stored.expiresAt > Date.now() + TOKEN_REFRESH_SKEW_MS;
        const nextSession = stillValid ? stored : await refreshSession(stored);
        if (!active) return;
        setSession(nextSession);
        setError(null);
      } catch (restoreError) {
        clearStoredSession();
        const nextError =
          restoreError instanceof Error && (restoreError as Error & { error?: string }).error === 'login_required'
            ? null
            : restoreError instanceof Error
              ? restoreError
              : new Error('Unable to restore session.');
        if (!active) return;
        setSession(null);
        setError(nextError);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    restore().catch(() => {
      if (!active) return;
      setIsLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  const getAccessTokenSilently = async () => {
    if (!session) {
      throw createLoginRequiredError();
    }

    if (session.expiresAt > Date.now() + TOKEN_REFRESH_SKEW_MS) {
      return session.accessToken;
    }

    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = refreshSession(session).finally(() => {
        refreshPromiseRef.current = null;
      });
    }

    try {
      const nextSession = await refreshPromiseRef.current;
      setSession(nextSession);
      setError(null);
      return nextSession.accessToken;
    } catch {
      clearStoredSession();
      setSession(null);
      setError(null);
      throw createLoginRequiredError();
    }
  };

  const loginWithRedirect = async (options?: Record<string, unknown> & { appState?: { returnTo?: string } }) => {
    const returnTo =
      options?.appState?.returnTo ||
      `${window.location.pathname}${window.location.search}${window.location.hash}` ||
      DEFAULT_RETURN_TO;
    window.location.assign(buildLoginUrl(returnTo, resolveCurrentLoginLanguage(returnTo)));
  };

  const logout = (options?: { logoutParams?: { returnTo?: string } }) => {
    clearStoredSession();
    setSession(null);
    setError(null);
    const returnTo =
      options?.logoutParams?.returnTo ||
      buildLoginUrl(DEFAULT_RETURN_TO, resolveCurrentLoginLanguage(DEFAULT_RETURN_TO));
    window.location.assign(returnTo);
  };

  const loginWithPassword = async (
    email: string,
    password: string,
    options?: { remember?: boolean; language?: SupportedLanguage },
  ) => {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      // Message unchanged; the status rides along so the sign-in screen can
      // tell "wrong password" from "server had a problem".
      const failure = new Error(await readResponseMessage(response, 'Unable to sign in.')) as Error & { status?: number };
      failure.status = response.status;
      throw failure;
    }

    const payload = (await response.json()) as {
      accessToken: string;
      refreshToken: string;
      mustChangePassword: boolean;
      isPlatformAdmin?: boolean;
    };

    const user = await fetchCurrentUser(payload.accessToken);
    const nextSession: StoredSession = {
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken,
      user,
      expiresAt: deriveExpiry(payload.accessToken),
      remember: options?.remember ?? true,
    };

    persistSession(nextSession);
    setSession(nextSession);
    setError(null);

    return {
      mustChangePassword: payload.mustChangePassword,
      isPlatformAdmin: payload.isPlatformAdmin === true,
    };
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: Boolean(session?.accessToken),
      isLoading,
      error,
      user: session?.user,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
      loginWithPassword,
    }),
    [error, isLoading, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth0() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth0 must be used within Auth0Provider.');
  }
  return context;
}
